import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBackendToken } from '../api/client';
import { pageCacheScope } from '../services/pageCache';
import { getCanonicalContentPath } from '../utils/contentRoutes';
import { getCanonicalProfilePath } from '../utils/profileRoutes';

const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
let navigationRequest = 0;
export const cancelPendingCanonicalNavigation = () => { navigationRequest++; };

function legacyTarget(to) {
  const path = typeof to === 'string' ? to.split(/[?#]/)[0] : to?.pathname;
  const match = typeof path === 'string' && path.match(/^\/(profile|post|blog|poll|quiz|video|newspaper)\/([^/]+)\/?$/);
  if (!match) return null;
  let identifier;
  try { identifier = decodeURIComponent(match[2]); } catch { return null; }
  // Handles and typed public post IDs are already canonical. Do not fetch on
  // every click; only resolve links whose source lacked public identifiers.
  if (match[1] !== 'post' && !UUID.test(identifier)) return null;
  return { kind: match[1] === 'profile' ? 'profile' : 'post', identifier, path };
}

export default function useCanonicalNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  const current = useRef(location);
  const mounted = useRef(true);
  current.current = location;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  return useCallback((to, options) => {
    const request = ++navigationRequest;
    const target = legacyTarget(to);
    const go = value => options === undefined ? navigate(value) : navigate(value, options);
    if (!target) return go(to);

    const origin = current.current.key;
    const historyKey = window.history.state?.key;
    const token = getBackendToken();
    const scope = pageCacheScope(token);
    const isCurrent = () => mounted.current && request === navigationRequest
      && current.current.key === origin && window.history.state?.key === historyKey
      && (scope ? pageCacheScope(getBackendToken()) === scope : getBackendToken() === token);
    const resolve = async () => {
      try {
        const path = target.kind === 'profile'
          ? getCanonicalProfilePath(await (await import('../api/profiles')).profilesApi.get(target.identifier))
          : getCanonicalContentPath(await (await import('../api/posts')).postsApi.get(target.identifier));
        if (!isCurrent()) return;
        // Preserve notification parameters, fragments, state and replace/back
        // semantics. The page cache shares this response with the destination.
        return go(typeof to === 'string' ? path + to.slice(target.path.length) : { ...to, pathname: path });
      } catch (error) {
        // Let the destination display its normal unavailable/error view, but
        // never resume a navigation from an account that has been switched.
        if (isCurrent() && error?.code !== 'SESSION_CHANGED') return go(to);
      }
    };
    return resolve();
  }, [navigate]);
}
